import constants from "../../constants";
import { DeepPartial } from "../../lib/redux";
import { GlobalReducer } from "../../store/reducers";
import { DRIVE_STORAGE_PREFIX } from "../../store/storageConstants";
import { dispatcher, DispatcherEvents } from "../dispatcher";
import { StorageKeys } from "./storage";
import * as logger from "../logger";

/**
 * General storage structure in IndexedDb
 *  - DbName: "drive-store"
 *       - ObjectStore: "main"
 *           - Row     Key                             Value
 *           -  1      "geotabDrive_@store_users"     { "b1": {name: "Bob"}}
 *
 * Chosen for simplicity that is similar to LocalStorage key/value pairs.
 * In future, a db upgrade could help by isolating each reducer to its own object store.
 */

export const IDB_DB_NAME = "drive-store";
const DB_VERSION = 1;
const PRIMARY_OBJECT_STORE = "main";

/**
 * Singleton promise for the current IndexedDB open attempt/connection.
 *
 * This may be:
 * - `null` when no connection has been opened yet
 * - a pending promise while `indexedDB.open(...)` is in flight
 * - a resolved promise for the currently open database connection
 *
 * Any failure or external close must clear this cache so later callers can
 * retry with a fresh `indexedDB.open(...)` instead of reusing a stale or
 * permanently rejected promise.
 */
let dbPromise: Promise<IDBDatabase> | null = null;

// eslint-disable-next-line @typescript-eslint/no-extraneous-class
export class IndexedDbStorage {
    static async saveData(key: string, value: any): Promise<void> {
        try {
            await IndexedDbStorage.putToStore(key, value);
        } catch (e) {
            if (e instanceof DOMException && e.name === "DataCloneError") {
                // Some browsers throw DataCloneError synchronously from store.put(); others fire it
                // through tx.onerror. Either way, sanitize via JSON round-trip and retry once.
                logger.error(logger.Source.INDEXED_DB, `DataCloneError for ${ key }, retrying with JSON sanitization.`);
                let sanitized: any;
                try {
                    sanitized = JSON.parse(JSON.stringify(value));
                } catch (sanitizeError) {
                    logger.error(logger.Source.INDEXED_DB, `Failure to JSON-sanitize data for ${ key }! Error: ${ sanitizeError }`);
                    throw e;
                }
                return IndexedDbStorage.putToStore(key, sanitized);
            }
            throw e;
        }
    }

    static async retrieveData(storeName: string): Promise<any> {
        const db = await IndexedDbStorage.openDb();

        return new Promise<any>((resolve, reject) => {
            const tx = db.transaction(PRIMARY_OBJECT_STORE, "readonly");
            const store = tx.objectStore(PRIMARY_OBJECT_STORE);
            const request = store.get(storeName);

            tx.oncomplete = () => {
                // Silencing for now to reduce log noise. Something like a "debug" log level would be great.
                // logger.log(logger.Source.INDEXED_DB, `Retrieved data from ${ storeName }.`);
                resolve(request.result);
            };

            tx.onerror = () => {
                const error = tx.error;
                logger.error(logger.Source.INDEXED_DB, `Failure to retrieve data from ${ storeName }! Error: ${ IndexedDbStorage.describeError(error) }`);
                reject(error);
            };
            tx.onabort = () => {
                const error = tx.error || new DOMException("Transaction aborted", "AbortError");
                logger.error(logger.Source.INDEXED_DB, `Transaction aborted while retrieving ${ storeName }! Error: ${ IndexedDbStorage.describeError(error) }`);
                reject(error);
            };
        });
    }

    static async removeData(storeName: string): Promise<void> {
        const db = await IndexedDbStorage.openDb();
        const tx = db.transaction(PRIMARY_OBJECT_STORE, "readwrite");
        const store = tx.objectStore(PRIMARY_OBJECT_STORE);

        store.delete(storeName);
        return new Promise<void>((resolve, reject) => {
            tx.oncomplete = () => {
                logger.log(logger.Source.INDEXED_DB, `Removed data from ${ storeName }.`);
                resolve();
            };
            tx.onerror = () => {
                const error = tx.error;
                logger.error(logger.Source.INDEXED_DB, `Failure to remove data from ${ storeName }! Error: ${ IndexedDbStorage.describeError(error) }`);
                reject(tx.error);
            };
            tx.onabort = () => {
                const error = tx.error || new DOMException("Transaction aborted", "AbortError");
                logger.error(logger.Source.INDEXED_DB, `Transaction aborted while removing ${ storeName }! Error: ${ IndexedDbStorage.describeError(error) }`);
                reject(error);
            };
        });
    }

    static async close(): Promise<void> {
        return IndexedDbStorage.closeDb();
    }

    static async getEverythingFromIndexedDb(): Promise<DeepReadonly<DeepPartial<GlobalReducer>>> {
        const data = await IndexedDbStorage.retrieveMainStore("preload");
        let result = {};

        for (const key in data) {
            if (Object.prototype.hasOwnProperty.call(data, key)) {
                result[key] = data[key];
            }
        }

        return result;
    }

    static async getEntityCounts(): Promise<{ [storeName: string]: number }> {
        const allData = await IndexedDbStorage.retrieveMainStore("getEntityCounts");

        // First, get counts for all cache entries
        let entityCounts = Object.keys(allData)
            .filter(itemKey => itemKey.indexOf(DRIVE_STORAGE_PREFIX) === 0 &&
                (itemKey.indexOf("_cache_") !== -1))
            .sort()
            .map(key => {
                let value = allData[key];
                let obj: {} | null = null;
                try {
                    obj = value && typeof value === "string" ? JSON.parse(value) : value;
                } catch { }
                return {
                    key: key.replace(DRIVE_STORAGE_PREFIX, ""),
                    count: obj ? Object.keys(obj).length : 0
                };
            })
            .reduce((result, item) => {
                result[item.key] = item.count;
                return result;
            }, {} as { [key: string]: number });

        // Handle entities with nested dictionaries
        const entitiesWithNestedDictionaries = [
            StorageKeys.HOSANNOTATIONS_KEY,
            StorageKeys.USER_LOGS_KEY,
            StorageKeys.SHIPMENTS_KEY,
            StorageKeys.SYSTEM_LOGS_KEY,
            StorageKeys.DVIR_KEY,
            StorageKeys.DRIVER_CHANGES_KEY,
            StorageKeys.TRAILER_ATTACHMENTS_KEY,
            StorageKeys.DEVICE_LINKS_KEY
        ];

        entitiesWithNestedDictionaries.forEach(key => {
            let value = allData[DRIVE_STORAGE_PREFIX + key];
            let obj: {} | null = null;
            try {
                obj = value && typeof value === "string" ? JSON.parse(value) : value;
            } catch { }
            let valueKeys = obj ? Object.keys(obj) : [];

            entityCounts[`${ key }_users`] = valueKeys.length;
            valueKeys.forEach(user => {
                entityCounts[`${ key }_${ user }`] = Object.keys((obj || {})[user] || {}).length;
            });
        });

        return entityCounts;
    }

    static async getSizeDistribution(): Promise<IIdDictionary<string>> {
        const getSizeFromLength = function (length: number) {
            let bits = length * 16;
            let value: number | string;
            let unit: string;

            if (bits >= 1025 * 512) {
                value = (bits / 8 / 1024 / 1024).toFixed(2);
                unit = "MB";
            } else if (bits >= 512) {
                value = (bits / 8 / 1024).toFixed(2);
                unit = "KB";
            } else {
                value = (bits / 8);
                unit = "B";
            }

            return `${ value }${ unit }`;
        };

        const allData = await IndexedDbStorage.retrieveMainStore("getSizeDistribution");

        return Object.keys(allData)
            .map(key => {
                let value = allData[key];
                let stringValue = value ? (typeof value === "string" ? value : JSON.stringify(value)) : "";

                return {
                    key,
                    size: stringValue.length,
                    value: stringValue
                };
            })
            .sort((item1, item2) => item1.size > item2.size ? -1 : 1)
            // let's return sizes for 20 the most expensive keys
            .slice(0, 20)
            .reduce((result, item) => {
                result[item.key] = getSizeFromLength(item.size);
                if (item.key.indexOf("@store_data") !== -1 && item.value) {
                    try {
                        let valueObj = JSON.parse(item.value);
                        for (let storageKey in valueObj) {
                            if (!Object.prototype.hasOwnProperty.call(valueObj, storageKey)) {
                                continue;
                            }

                            let valObjectSize = valueObj ? JSON.stringify(valueObj[storageKey]).length : 0;
                            result[`${ item.key } > ${ storageKey }`] = getSizeFromLength(valObjectSize);
                        }
                    } catch { }
                }
                return result;
            }, {} as IIdDictionary<string>);
    }

    static async size(): Promise<number> {
        const textEncoder = new TextEncoder();
        let totalBytes = 0;

        const allData = await IndexedDbStorage.retrieveMainStore("size");

        for (const key in allData) {
            if (!Object.prototype.hasOwnProperty.call(allData, key)) {
                continue;
            }

            const value = allData[key];
            const stringValue = value ? (typeof value === "string" ? value : JSON.stringify(value)) : "";

            // TextEncoder is the best way to get accurate UTF-8 byte sizes
            totalBytes += textEncoder.encode(key).length;
            totalBytes += textEncoder.encode(stringValue).length;
        }

        return totalBytes / 1024;
    }

    static async clear(removePersisted = false, overrideUser?: string, overridePrefix?: string): Promise<void> {
        const walkmePrefixes = ["walkMe_", "wm-"];
        const getPersistentStoragePrefixes = () => [`${ DRIVE_STORAGE_PREFIX }@`, ...walkmePrefixes];

        const db = await IndexedDbStorage.openDb();
        const tx = db.transaction(PRIMARY_OBJECT_STORE, "readwrite");
        const store = tx.objectStore(PRIMARY_OBJECT_STORE);
        const request = store.openCursor();

        const keysToDelete: string[] = [];
        const ignoredPattern = new RegExp(`^(${ getPersistentStoragePrefixes().join("|") })`);

        // Helper function to get prefix (mimics getPrefix from storage.ts)
        const getPrefix = (key: string, userId?: string): string => {
            if (key && key.replace(DRIVE_STORAGE_PREFIX, "").match(/^@/)) {
                // Don't use a user name prefix if it's a non-user value
                return DRIVE_STORAGE_PREFIX;
            }

            if (!userId) {
                // Return just the storage prefix when no user is specified
                return DRIVE_STORAGE_PREFIX;
            }

            // Use the passed in userId to create user-specific prefix
            return `${ DRIVE_STORAGE_PREFIX }${ userId }_`;
        };

        return new Promise<void>((resolve, reject) => {
            let transactionSettled = false;

            request.onsuccess = function(event) {
                if (transactionSettled) {
                    return;
                }
                let cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
                if (cursor) {
                    const key = String(cursor.key);

                    // Skip if we shouldn't remove persisted keys and this key matches the ignored pattern
                    // Also skip driveAPICredentials
                    if ((!removePersisted && key.match(ignoredPattern)) || key.indexOf("driveAPICredentials") !== -1) {
                        cursor.continue();
                        return;
                    }

                    // Determine the prefix to use (matches localStorage logic)
                    let prefix = !overridePrefix ? getPrefix(key, overrideUser) : overridePrefix;

                    // If key contains the prefix or doesn't have the storage prefix at all, mark for deletion
                    if (key.indexOf(prefix) !== -1 || key.indexOf(DRIVE_STORAGE_PREFIX) === -1) {
                        keysToDelete.push(key);
                    }

                    cursor.continue();
                }
            };

            request.onerror = function(_) {
                logger.error(logger.Source.INDEXED_DB, "Failure to retrieve keys during clear operation");
                reject(request.error);
            };

            tx.oncomplete = () => {
                transactionSettled = true;
                // Now delete all the keys we collected in a single transaction
                if (keysToDelete.length > 0) {
                    logger.log(logger.Source.INDEXED_DB, `Clearing ${ keysToDelete.length } keys from IndexedDB.`);

                    const deleteTx = db.transaction(PRIMARY_OBJECT_STORE, "readwrite");
                    const deleteStore = deleteTx.objectStore(PRIMARY_OBJECT_STORE);

                    // Delete all keys in this single transaction
                    keysToDelete.forEach(key => deleteStore.delete(key));

                    deleteTx.oncomplete = () => {
                        logger.log(logger.Source.INDEXED_DB, `Successfully cleared ${ keysToDelete.length } keys from IndexedDB.`);
                        resolve();
                    };

                    deleteTx.onerror = () => {
                        logger.error(logger.Source.INDEXED_DB, `Failed to clear some keys from IndexedDB`);
                        reject(deleteTx.error);
                    };
                    deleteTx.onabort = () => {
                        const error = deleteTx.error || new DOMException("Transaction aborted", "AbortError");
                        logger.error(logger.Source.INDEXED_DB, `Transaction aborted while deleting cleared keys! Error: ${ IndexedDbStorage.describeError(error) }`);
                        reject(error);
                    };
                } else {
                    logger.log(logger.Source.INDEXED_DB, "No keys to clear from IndexedDB.");
                    resolve();
                }
            };

            tx.onerror = () => {
                transactionSettled = true;
                const error = tx.error;
                logger.error(logger.Source.INDEXED_DB, `Transaction error during clear operation! Error: ${ IndexedDbStorage.describeError(error) }`);
                reject(tx.error);
            };
            tx.onabort = () => {
                transactionSettled = true;
                const error = tx.error || new DOMException("Transaction aborted", "AbortError");
                logger.error(logger.Source.INDEXED_DB, `Transaction aborted during clear cursor operation! Error: ${ IndexedDbStorage.describeError(error) }`);
                reject(error);
            };
        });
    }

    static isIndexDbSupported(): boolean {
        try {
            return typeof indexedDB !== "undefined" && indexedDB !== null;
        } catch (error) {
            // Some browsers throw when accessing indexedDB in certain modes (e.g., Firefox private browsing)
            return false;
        }
    }

    /**
     * Checks if our specific IndexedDB database (IDB_DB_NAME) exists.
     *
     * Tries multiple approaches in order:
     * 1. Modern databases() API (Chrome 60+, Firefox 68+) - Checks all databases for IDB_DB_NAME
     * 2. Legacy webkitGetDatabaseNames (Chrome 11-76, Safari) - Checks all databases for IDB_DB_NAME
     * 3. Fallback: Try to open IDB_DB_NAME directly to check if it exists
     *
     * Returns true if the database exists, false otherwise.
     * Always returns a boolean, never throws - returns false on any error.
     */
    static async checkOurDatabaseExists(): Promise<boolean> {
        if (!IndexedDbStorage.isIndexDbSupported()) {
            return false;
        }

        // Try modern databases() API (not supported in all browsers)
        if (typeof indexedDB.databases === "function") {
            try {
                const databases = await indexedDB.databases();
                // Check if our database is in the list
                return databases.some(db => db.name === IDB_DB_NAME);
            } catch (error) {
                logger.log(
                    logger.Source.INDEXED_DB,
                    `IndexedDB.databases() API failed: ${ error }`
                );
                // Fall through to next fallback
            }
        }

        // Try legacy webkitGetDatabaseNames (Chrome 11-76, Safari)
        // @ts-expect-error - webkitGetDatabaseNames is not in TypeScript definitions
        if (typeof indexedDB.webkitGetDatabaseNames === "function") {
            try {
                // @ts-expect-error - webkitGetDatabaseNames is not in TypeScript definitions
                const request = indexedDB.webkitGetDatabaseNames();
                return await new Promise((resolve) => {
                    request.onsuccess = () => {
                        const dbNames: DOMStringList = request.result;
                        // Check if our database is in the list
                        for (let i = 0; i < dbNames.length; i++) {
                            if (dbNames[i] === IDB_DB_NAME) {
                                resolve(true);
                                return;
                            }
                        }
                        // Our database not found
                        resolve(false);
                    };
                    request.onerror = () => {
                        logger.log(
                            logger.Source.INDEXED_DB,
                            `IndexedDB.webkitGetDatabaseNames failed: ${ request.error }`
                        );
                        resolve(false);
                    };
                });
            } catch (error) {
                logger.log(
                    logger.Source.INDEXED_DB,
                    `IndexedDB.webkitGetDatabaseNames threw error: ${ error }`
                );
                // Fall through to final fallback
            }
        }

        // Final fallback: Try to open our database directly to check if it exists
        return new Promise((resolve) => {
            try {
                const request = indexedDB.open(IDB_DB_NAME);
                request.onsuccess = () => {
                    const db = request.result;
                    const exists = db.version > 0;
                    db.close();
                    resolve(exists);
                };
                request.onerror = () => {
                    resolve(false);
                };
            } catch (error) {
                resolve(false);
            }
        });
    }

    static async clearAllData(retryCount = 0, maxRetries = 3): Promise<void> {
        const retryDelayMs = 1000;

        logger.log(logger.Source.INDEXED_DB, `Clearing all IndexedDB data.${ retryCount > 0 ? ` (Retry ${ retryCount }/${ maxRetries })` : "" }`);
        const dbExists = await IndexedDbStorage.checkOurDatabaseExists();
        if (!dbExists) {
            logger.log(logger.Source.INDEXED_DB, `No data to clear in IndexedDB database ${ IDB_DB_NAME }.`);
            return Promise.resolve();
        }

        // First close open connection or else deleteDatabase will hang
        await IndexedDbStorage.closeDb();

        logger.log(logger.Source.INDEXED_DB, "Closed connection.");

        return new Promise((resolve, reject) => {
            let request = indexedDB.deleteDatabase(IDB_DB_NAME);
            request.onsuccess = () => {
                logger.log(logger.Source.INDEXED_DB, `Successfully deleted IndexedDB database ${ IDB_DB_NAME }.`);
                resolve();
            };
            request.onerror = () => {
                logger.error(logger.Source.INDEXED_DB, `Failed to delete IndexedDB database ${ IDB_DB_NAME }.`);
                reject(request.error);
            };
            request.onblocked = () => {
                logger.log(logger.Source.INDEXED_DB, `Delete blocked for IndexedDB database ${ IDB_DB_NAME }. There may be open connections in other tabs.`);

                if (retryCount < maxRetries) {
                    logger.log(logger.Source.INDEXED_DB, `Retrying in ${ retryDelayMs * (retryCount + 1) }ms...`);
                    setTimeout(() => {
                        IndexedDbStorage.clearAllData(retryCount + 1, maxRetries)
                            .then(() => resolve())
                            .catch(error => reject(error));
                    }, retryDelayMs * (retryCount + 1));
                } else {
                    logger.error(logger.Source.INDEXED_DB, `Failed to delete database after ${ maxRetries } retries. Database is still blocked.`);
                    reject(new Error(`Database deletion blocked after ${ maxRetries } retries - there may be open connections in other tabs`));
                }
            };
        });
    }

    static async removeAllKeysContainingStrings(stringsToRemove: string[]): Promise<void> {
        const keysInIndexedDb = await IndexedDbStorage.getEverythingFromIndexedDb();

        const keysToDelete: string[] = [];
        for (const key of Object.keys(keysInIndexedDb)) {
            if (stringsToRemove.some(str => key.indexOf(str) !== -1)) {
                keysToDelete.push(key);
            }
        }

        if (keysToDelete.length === 0) {
            return;
        }

        const db = await IndexedDbStorage.openDb();
        const tx = db.transaction(PRIMARY_OBJECT_STORE, "readwrite");
        const store = tx.objectStore(PRIMARY_OBJECT_STORE);

        // Delete all keys in a single transaction
        keysToDelete.forEach(key => store.delete(key));

        return new Promise<void>((resolve, reject) => {
            tx.oncomplete = () => resolve();
            tx.onerror = () => reject(tx.error);
            tx.onabort = () => {
                const error = tx.error || new DOMException("Transaction aborted", "AbortError");
                logger.error(logger.Source.INDEXED_DB, `Transaction aborted while removing keys! Error: ${ IndexedDbStorage.describeError(error) }`);
                reject(error);
            };
        });
    }

    /**
     * Opens a transaction and puts a value at the given key.
     * Propagates any error (including DataCloneError) as a rejection so saveData can retry.
     */
    private static async putToStore(key: string, value: any): Promise<void> {
        const db = await IndexedDbStorage.openDb();
        const tx = db.transaction(PRIMARY_OBJECT_STORE, "readwrite");
        const store = tx.objectStore(PRIMARY_OBJECT_STORE);

        try {
            store.put(value, key);
        } catch (e) {
            // Synchronous throw (e.g. DataCloneError on iOS WKWebView). Abort the idle
            // transaction before re-throwing so it doesn't linger open.
            tx.abort();
            throw e;
        }

        return new Promise<void>((resolve, reject) => {
            let settled = false;

            tx.oncomplete = () => {
                settled = true;
                resolve();
            };
            tx.onerror = () => {
                settled = true;
                const error = tx.error;
                if (error && (error.name === constants.QUOTA_EXCEEDED_ERROR)) {
                    // Trigger event to display dialog and do a full sync
                    dispatcher.trigger(DispatcherEvents.LocalStorageFull);
                }
                if (error?.name !== "DataCloneError") {
                    logger.error(logger.Source.INDEXED_DB, `Failure to store data to ${ key }! Error: ${ IndexedDbStorage.describeError(error) }`);
                }
                reject(error);
            };
            tx.onabort = () => {
                if (settled) {
                    // onerror already fired and rejected — onabort is a follow-on event, suppress it.
                    return;
                }
                const error = tx.error || new DOMException("Transaction aborted", "AbortError");
                logger.error(logger.Source.INDEXED_DB, `Transaction aborted for ${ key }! Error: ${ IndexedDbStorage.describeError(error) }`);
                reject(error);
            };
        });
    }

    private static openDb(): Promise<IDBDatabase> {
        if (dbPromise) {
            return dbPromise;
        }

        let cachedDbPromise: Promise<IDBDatabase>;
        const openRequestPromise = new Promise<IDBDatabase>((resolve, reject) => {
            const request = indexedDB.open(IDB_DB_NAME, DB_VERSION);
            request.onupgradeneeded = () => {
                const db = request.result;
                if (!db.objectStoreNames.contains(PRIMARY_OBJECT_STORE)) {
                    db.createObjectStore(PRIMARY_OBJECT_STORE, { autoIncrement: false });
                }
            };
            request.onsuccess = () => {
                const db = request.result;
                db.onclose = () => {
                    if (dbPromise === cachedDbPromise) {
                        dbPromise = null;
                    }
                };
                resolve(db);
            };
            request.onerror = () => reject(request.error);
        });

        cachedDbPromise = openRequestPromise.catch(error => {
            if (dbPromise === cachedDbPromise) {
                dbPromise = null;
            }
            throw error;
        });

        dbPromise = cachedDbPromise;
        return dbPromise;
    }

    private static async closeDb() {
        const pendingDbPromise = dbPromise;
        dbPromise = null;

        if (pendingDbPromise) {
            try {
                const db = await pendingDbPromise;
                db.close();
            } catch {
                // The open attempt already failed. Leave the cache cleared so
                // the next caller can open a fresh connection.
            }
        }
    }

    private static describeError(error: unknown): string {
        if (error === null || error === undefined) {
            return String(error);
        }
        if (typeof error !== "object") {
            return String(error);
        }
        const props = {};
        for (const key of Object.getOwnPropertyNames(error)) {
            try {
                props[key] = (error as any)[key];
            } catch { }
        }
        try {
            return JSON.stringify(props);
        } catch {
            return String(error);
        }
    }

    private static async retrieveMainStore(caller: string) {
        const db = await IndexedDbStorage.openDb();
        const tx = db.transaction(PRIMARY_OBJECT_STORE, "readonly");
        const store = tx.objectStore(PRIMARY_OBJECT_STORE);
        const request = store.openCursor();
        let result = {};

        return new Promise<any>((resolve, reject) => {
            let transactionSettled = false;

            request.onsuccess = function(event) {
                if (transactionSettled) {
                    return;
                }
                let cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
                if (cursor) {
                    result[String(cursor.key)] = cursor.value;
                    cursor.continue();
                }
            };

            tx.oncomplete = () => {
                transactionSettled = true;
                resolve(result);
            };
            tx.onerror = () => {
                transactionSettled = true;
                const error = tx.error;
                logger.error(logger.Source.INDEXED_DB, `Failure to retrieve data from main store [${ caller }]: ${ IndexedDbStorage.describeError(error) }`);
                reject(error);
            };
            tx.onabort = () => {
                transactionSettled = true;
                const error = tx.error || new DOMException("Transaction aborted", "AbortError");
                logger.error(logger.Source.INDEXED_DB, `Transaction aborted while retrieving main store [${ caller }]! Error: ${ IndexedDbStorage.describeError(error) }`);
                reject(error);
            };
        });
    }
}
